import { ResearchResultItemDto } from '../../../types/admin-types'
import moment from 'moment'
import React from 'react'
import ReactMarkdown from 'react-markdown'

// Use moment instead of date-fns

// Using date-fns for formatting, assuming it's available

interface ResearchResultsTableProps {
    results: ResearchResultItemDto[]
    isLoading: boolean
    selectedResultIds: Set<string>
    onToggleSelection: (id: string) => void
    onSelectAll: (allVisibleIds: string[]) => void
    allVisibleIds: string[]
}

const formatDate = (dateInput: Date | string | null | undefined): string => {
    if (!dateInput) return 'N/A'
    try {
        // Moment can parse various inputs
        const date = moment(dateInput)
        if (!date.isValid()) {
            throw new Error('Invalid date input')
        }
        return date.format('MMM D, YYYY h:mm A') // Format like: Jan 5, 2024 3:00 PM
    } catch (e) {
        console.error('Error formatting date:', e)
        return 'Invalid Date'
    }
}

const ResearchResultsTable: React.FC<ResearchResultsTableProps> = ({
    results,
    isLoading,
    selectedResultIds,
    onToggleSelection,
    onSelectAll,
    allVisibleIds,
}) => {
    const areAllVisibleSelected =
        allVisibleIds.length > 0 && allVisibleIds.every((id) => selectedResultIds.has(id))

    return (
        <div className="overflow-x-auto bg-white rounded shadow border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-100">
                    <tr>
                        <th scope="col" className="p-4 align-top">
                            <input
                                type="checkbox"
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                checked={areAllVisibleSelected}
                                onChange={() => onSelectAll(allVisibleIds)}
                                disabled={isLoading || allVisibleIds.length === 0}
                            />
                        </th>
                        <th
                            scope="col"
                            className="px-6 py-4 text-left text-xs font-medium text-gray-600 uppercase tracking-wider align-top min-w-[200px]"
                        >
                            Title
                        </th>
                        <th
                            scope="col"
                            className="px-6 py-4 text-left text-xs font-medium text-gray-600 uppercase tracking-wider align-top"
                        >
                            URL
                        </th>
                        <th
                            scope="col"
                            className="px-6 py-4 text-left text-xs font-medium text-gray-600 uppercase tracking-wider align-top min-w-[600px]"
                        >
                            Summary
                        </th>
                        <th
                            scope="col"
                            className="px-6 py-4 text-left text-xs font-medium text-gray-600 uppercase tracking-wider align-top min-w-[400px]"
                        >
                            Relevance Reason
                        </th>
                        <th
                            scope="col"
                            className="px-6 py-4 text-left text-xs font-medium text-gray-600 uppercase tracking-wider align-top min-w-[150px]"
                        >
                            Published
                        </th>
                        <th
                            scope="col"
                            className="px-6 py-4 text-left text-xs font-medium text-gray-600 uppercase tracking-wider align-top min-w-[150px]"
                        >
                            Created
                        </th>
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                    {isLoading ? (
                        <tr>
                            <td colSpan={7} className="px-6 py-6 text-center text-gray-500">
                                Loading...
                            </td>
                        </tr>
                    ) : results.length === 0 ? (
                        <tr>
                            <td colSpan={7} className="px-6 py-6 text-center text-gray-500">
                                No research results found for this user.
                            </td>
                        </tr>
                    ) : (
                        results.map((item, index) => (
                            <tr
                                key={item.id}
                                className={`align-top ${
                                    index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                                } ${
                                    selectedResultIds.has(item.id)
                                        ? '!bg-blue-100'
                                        : 'hover:bg-blue-50'
                                }`}
                            >
                                <td className="p-4 align-top">
                                    <input
                                        type="checkbox"
                                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        checked={selectedResultIds.has(item.id)}
                                        onChange={() => onToggleSelection(item.id)}
                                    />
                                </td>
                                <td className="px-6 py-4 text-sm font-medium text-gray-900 align-top">
                                    {item.title}
                                </td>
                                <td className="px-6 py-4 text-sm text-blue-600 align-top break-words">
                                    <a
                                        href={item.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="hover:underline"
                                        title={item.url}
                                    >
                                        {item.url}
                                    </a>
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-600 align-top">
                                    <ReactMarkdown>{item.summary}</ReactMarkdown>
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-600 align-top">
                                    <span title={item.relevance_reason}>
                                        {item.relevance_reason}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 align-top">
                                    {formatDate(item.published_at)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 align-top">
                                    {formatDate(item.created_at)}
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    )
}

export default ResearchResultsTable
